import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'previewContainer',
    'previewButton',
    'leftInput',
    'topInput',
    'horizontalSelect',
    'verticalSelect',
    'previewModeHorizontal',
    'previewModeVertical',
    'textPanel',
    'imagePanel',
  ]

  static values = {
    contentTypeName: { type: String, default: '' },
    destinationTypeName: { type: String, default: '' },
    textContentType: { type: String, default: 'Text' },
    imageContentType: { type: String, default: 'Image' },
  }

  connect() {
    this.previewWidth = 400
    this.previewHeight = 150
    this.setOrientationFromMode()
    this.applyHorizontal(this.horizontalSelectTarget.value, true)
    this.applyVertical(this.verticalSelectTarget.value, true)
    this.updateOrientation()

    this.onHorizontalSelectChange = (e) => this.applyHorizontal(e.target.value, false)
    this.onVerticalSelectChange = (e) => this.applyVertical(e.target.value, false)
    this.onLeftInputChange = (e) => this.applyHorizontalOffset(e.target.value)
    this.onTopInputChange = (e) => this.applyVerticalOffset(e.target.value)
    this.onPreviewModeHorizontal = () => {
      this.previewWidth = 400
      this.previewHeight = 150
      this.updateOrientation()
    }
    this.onPreviewModeVertical = () => {
      this.previewWidth = 250
      this.previewHeight = 350
      this.updateOrientation()
    }
    this.onElementChange = (e) => this.delegatedChange(e)

    this.horizontalSelectTarget.addEventListener('change', this.onHorizontalSelectChange)
    this.verticalSelectTarget.addEventListener('change', this.onVerticalSelectChange)
    this.leftInputTarget.addEventListener('change', this.onLeftInputChange)
    this.topInputTarget.addEventListener('change', this.onTopInputChange)
    this.previewModeHorizontalTarget.addEventListener('change', this.onPreviewModeHorizontal)
    this.previewModeVerticalTarget.addEventListener('change', this.onPreviewModeVertical)

    if (this.contentTypeNameValue || this.destinationTypeNameValue) {
      this.element.addEventListener('change', this.onElementChange)
    }
    if (this.destinationTypeNameValue) {
      this.syncDestinations()
    }
  }

  disconnect() {
    this.horizontalSelectTarget.removeEventListener('change', this.onHorizontalSelectChange)
    this.verticalSelectTarget.removeEventListener('change', this.onVerticalSelectChange)
    this.leftInputTarget.removeEventListener('change', this.onLeftInputChange)
    this.topInputTarget.removeEventListener('change', this.onTopInputChange)
    this.previewModeHorizontalTarget.removeEventListener('change', this.onPreviewModeHorizontal)
    this.previewModeVerticalTarget.removeEventListener('change', this.onPreviewModeVertical)
    this.element.removeEventListener('change', this.onElementChange)
  }

  setOrientationFromMode() {
    if (this.hasPreviewModeVerticalTarget && this.previewModeVerticalTarget.checked) {
      this.previewWidth = 250
      this.previewHeight = 350
    } else {
      this.previewWidth = 400
      this.previewHeight = 150
    }
  }

  updateOrientation() {
    this.previewContainerTarget.style.width = `${this.previewWidth}px`
    this.previewContainerTarget.style.height = `${this.previewHeight}px`
    this.applyHorizontalOffset(this.leftInputTarget.value)
    this.applyVerticalOffset(this.topInputTarget.value)
  }

  applyHorizontal(value, initialLoad) {
    if (!initialLoad) {
      this.leftInputTarget.value = '0'
    }

    const c = this.previewContainerTarget
    c.classList.remove('justify-start', 'justify-center', 'justify-end')

    if (value === 'left') {
      c.classList.add('justify-start')
      this.leftInputTarget.disabled = false
    } else if (value === 'middle') {
      c.classList.add('justify-center')
      this.leftInputTarget.disabled = true
      this.leftInputTarget.value = '50'
    } else {
      c.classList.add('justify-end')
      this.leftInputTarget.disabled = false
    }

    const b = this.previewButtonTarget
    b.style.marginLeft = '0px'
    b.style.marginRight = '0px'
  }

  applyVertical(value, initialLoad) {
    if (!initialLoad) {
      this.topInputTarget.value = '0'
    }

    const c = this.previewContainerTarget
    c.classList.remove('items-start', 'items-center', 'items-end')

    if (value === 'top') {
      c.classList.add('items-start')
      this.topInputTarget.disabled = false
    } else if (value === 'middle') {
      c.classList.add('items-center')
      this.topInputTarget.disabled = true
      this.topInputTarget.value = '50'
    } else {
      c.classList.add('items-end')
      this.topInputTarget.disabled = false
    }

    const b = this.previewButtonTarget
    b.style.marginTop = '0px'
    b.style.marginBottom = '0px'
  }

  applyHorizontalOffset(value) {
    const side = this.horizontalSelectTarget.value
    let n = parseInt(String(value), 10)
    if (Number.isNaN(n)) {
      n = 0
    }
    const containerWidth = this.previewContainerTarget.getBoundingClientRect().width
    const buttonWidth = this.previewButtonTarget.getBoundingClientRect().width
    const pos = Math.max(0, Math.min(containerWidth - buttonWidth, (containerWidth / 100) * n))

    const b = this.previewButtonTarget
    b.style.setProperty('margin-left', '0px')
    b.style.setProperty('margin-right', '0px')
    b.style.setProperty(`margin-${side}`, `${pos}px`)
  }

  applyVerticalOffset(value) {
    const side = this.verticalSelectTarget.value
    let n = parseInt(String(value), 10)
    if (Number.isNaN(n)) {
      n = 0
    }
    const containerHeight = this.previewContainerTarget.getBoundingClientRect().height
    const buttonHeight = this.previewButtonTarget.getBoundingClientRect().height
    const pos = Math.max(0, Math.min(containerHeight - buttonHeight, (containerHeight / 100) * n))

    const b = this.previewButtonTarget
    b.style.setProperty('margin-top', '0px')
    b.style.setProperty('margin-bottom', '0px')
    b.style.setProperty(`margin-${side}`, `${pos}px`)
  }

  delegatedChange(e) {
    const t = e.target
    if (!t || typeof t.getAttribute !== 'function') {
      return
    }
    const name = t.getAttribute('name') || ''
    if (
      this.contentTypeNameValue
      && name === this.contentTypeNameValue
      && t.getAttribute('type') === 'radio'
    ) {
      this.updateContentTypePanels(t.value)
    }
    if (
      this.destinationTypeNameValue
      && name === this.destinationTypeNameValue
      && t.getAttribute('type') === 'radio'
    ) {
      this.syncDestinations()
    }
  }

  updateContentTypePanels(selected) {
    if (!this.hasTextPanelTarget || !this.hasImagePanelTarget) {
      return
    }
    if (selected === this.textContentTypeValue) {
      this.textPanelTarget.classList.remove('hidden')
      this.imagePanelTarget.classList.add('hidden')
    } else {
      this.textPanelTarget.classList.add('hidden')
      this.imagePanelTarget.classList.remove('hidden')
    }
  }

  syncDestinations() {
    const website = this.element.querySelector(
      '#website-destination, #secondary-website-destination'
    )
    const playlist = this.element.querySelector(
      '#playlist-destination, #secondary-playlist-destination'
    )
    if (!website || !playlist) {
      return
    }
    const n = this.destinationTypeNameValue
    let v = null
    this.element.querySelectorAll('input[type="radio"]').forEach((r) => {
      if (r.getAttribute('name') === n && r.checked) {
        v = r.value
      }
    })
    const isWebsite = v === null || v === 'website'
    if (isWebsite) {
      website.classList.remove('hidden')
      playlist.classList.add('hidden')
    } else {
      website.classList.add('hidden')
      playlist.classList.remove('hidden')
    }
  }
}
